import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useJ5M2, ItemState } from './hooks/useJ5M2';
import { j5m2Api } from '../../services/j5m2Api';
import { BatchAcceptanceBridge, pwj5m2f1AcceptanceManifest } from '../../features/acceptance';
import './J5M2Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const fmtAmount = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const J5M2Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleExecute,
  } = useJ5M2();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆樣品繳庫單？`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const columns: ColumnsType<ItemState> = [
    {
      title: <span data-contract-field="fj120_01_selected">沖銷</span>,
      dataIndex: 'selected',
      key: 'selected',
      width: 60,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fj120_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: <span data-contract-field="fj120_01">繳庫單號</span>,
      dataIndex: ['item', 'fj120_01'],
      key: 'fj120_01',
      width: 120,
    },
    {
      title: <span data-contract-field="fj120_11">繳庫日期</span>,
      dataIndex: ['item', 'fj120_11'],
      key: 'fj120_11',
      width: 120,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: <span data-contract-field="fj120_12">倉庫</span>,
      dataIndex: ['item', 'fj120_12'],
      key: 'fj120_12',
      width: 140,
    },
    {
      title: <span data-contract-field="fj120_14">部門</span>,
      dataIndex: ['item', 'fj120_14'],
      key: 'fj120_14',
      width: 140,
    },
    {
      title: <span data-contract-field="fj120_15">金額</span>,
      dataIndex: ['item', 'fj120_15'],
      key: 'fj120_15',
      width: 120,
      align: 'right',
      render: (v: number | null) => fmtAmount(v),
    },
  ];

  return (
    <div className="j5m2-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>研發管理</a>
              <span>/</span>
              <span className="current">樣品繳庫單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>樣品繳庫單核准沖銷</h1>
            <span className="badge">J5M2</span>
          </div>
        </div>

        <div className="query-bar">
          <label htmlFor="fj120_01_start">繳庫單號（起）</label>
          <Input
            id="fj120_01_start"
            data-contract-field="fj120_01_start"
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <label htmlFor="fj120_01_end">繳庫單號（迄）</label>
          <Input
            id="fj120_01_end"
            data-contract-field="fj120_01_end"
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button
            type="primary"
            data-acceptance-reveal-fields
            data-acceptance-reveal-fill="fj120_01_start"
            onClick={onQuery}
            loading={loading}
          >
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>，已勾選 <strong>{selectedCount}</strong> 筆</span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fj120_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入繳庫單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwj5m2f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((row) => row.selected).map((row) => row.item.fj120_01)}
          keyOf={(row) => row.item.fj120_01}
          queryExact={(key) => j5m2Api.query(key, key)}
          lastApiResponse={items}
          loading={loading || executeLoading}
          onAutoFill={() => items[0] && handleToggleItem(items[0].item.fj120_01, true)}
          autoFillDisabled={items.length === 0}
          screenDraft={{ startNo, endNo }}
        />
      </div>
    </div>
  );
};

export default J5M2Form;
